// pages/ReviewList.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { deleteReviewAPI, getAllReviewsAPI } from "../../../../api/reviewAPis.ts";
import {
  BlockBetween,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  PreviewCard,
  ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const ReviewList = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);

  const handleDelete = async (review) => {
    try {
      await deleteReviewAPI({ reviewId: review._id });
      setReviews((prev) => prev.filter((r) => r._id !== review._id));
      toast.success("Review deleted successfully");
    } catch (error) {
      toast.error("Failed to delete review: " + (error.message || "Unknown error"));
      console.error("Delete error:", error);
    }
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await getAllReviewsAPI();
        const fetchedReviews = response.data.data; // Adjusted for your response structure
        setReviews(fetchedReviews);
      } catch (error) {
        console.error("Failed to fetch reviews:", error);
        toast.error("Failed to fetch reviews");
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const columns = [
    {
      name: "Product",
      selector: (row) => row.productId.name || "N/A",
      cell: (row) => (
        <div className="d-flex align-items-center m-1 g-2" style={{ cursor: "pointer" }}>
          <img
            src={row.productId.images?.[0] || "https://via.placeholder.com/50"}
            alt={row.productId.name}
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "5%",
              objectFit: "cover",
            }}
          />
          <div
            className="fw-bold cursor-pointer"
            onClick={() => navigate(`/product/${row.productId._id}`)}
          >
            {row.productId.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      name: "User",
      selector: (row) => `${row.userId.first_name} ${row.userId.last_name}`,
      cell: (row) => (
        <span className="text-muted">
          {row.userId ? `${row.userId.first_name} ${row.userId.last_name}` : "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Rating",
      selector: (row) => row.rating,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span className="text-warning">★</span>
          <span className="fw-bold ms-1">{row.rating}/5</span>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Comment",
      selector: (row) => row.comment || "",
      cell: (row) => (
        <span className="text-muted">
          {row.comment?.substring(0, 50) || "No comment"}
          {row.comment?.length > 50 ? "..." : ""}
        </span>
      ),
    },
    {
      name: "Date",
      selector: (row) => row.createdAt,
      cell: (row) => (
        <span>{new Date(row.createdAt).toLocaleDateString()}</span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => {
              setSelectedReview(row);
              toggleDeleteModal();
            }}
          >
            <i className="bi bi-trash-fill"></i> Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Head title="Review List" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Review List
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have a total of {reviews.length} reviews.</p>
              </BlockDes>
            </BlockHeadContent>
            {/* <BlockHeadContent>
              <Button color="success" onClick={() => navigate("/add-review")}>
                Add Review
              </Button>
            </BlockHeadContent> */}
          </BlockBetween>
        </BlockHead>
        <BlockContent>
          <PreviewCard>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <div className="text-center">
                  <div
                    className="spinner-grow text-primary"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading reviews, please wait...</p>
                </div>
              </div>
            ) : (
              <ReactDataTable
                data={reviews}
                columns={columns}
                pagination
                actions // Enables export buttons
                expandableRows // Makes it responsive for mobile
              />
            )}
          </PreviewCard>
        </BlockContent>
      </Content>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        toggle={toggleDeleteModal}
        onConfirm={handleDelete}
        item={selectedReview}
        itemName="review"
      />
    </React.Fragment>
  );
};

export default ReviewList;